'use client';

import './desktop.scss';
import { ReactNode, useEffect } from "react";
import HeaderDesktop from "./HeaderDesktop";
import FooterDesktop from "./FooterDesktop";
import { useContentSize, useSetContentSize } from "@/components/SizeProvider";
import SiderRight from "./SiderRight";
import SiderLeft from "./SiderLeft";
import { ProjectModel } from "@/lib/models/Project";
import { calculateContentSize } from "@/lib/utils/contentSize";

interface Props {
  children: ReactNode;
  projects: ProjectModel[];
}

export default function DesktopLayout({ children, projects }: Props) {
  const contentSize = useContentSize();
  const setContentSize = useSetContentSize();

  useEffect(() => {
    const syncContentSize = () => {
      setContentSize(calculateContentSize());
    };

    syncContentSize();
    window.addEventListener("resize", syncContentSize);

    return () => {
      window.removeEventListener("resize", syncContentSize);
    }
  }, [setContentSize]);

  const isMeasured = contentSize.width > 0 && contentSize.height > 0;

  return (
    <div style={{ position: 'relative', minHeight: '100vh' }}>
      {/* Loading overlay — hides the flashbang until layout is measured */}
      <div
        className="desktop-loading-overlay"
        style={{
          opacity: isMeasured ? 0 : 1,
          pointerEvents: isMeasured ? 'none' : 'auto',
          transition: 'opacity 300ms ease-in-out',
        }}
      />

      {/* Dirt background texture behind desktop. */}
      {/* TODO: Remove when the video approach has been confirmed to work well. */}
      {/* <div className="dirt-bg-texture" /> */}
      <video
        className="dirt-bg-texture-video"
        src="/assets/gif/DIRT.webm"
        autoPlay
        loop
        muted
        playsInline
      />

      {/* Fixed element with size: (contentSize.width, contentSize.height), positioned at center */}
      <div 
        className="layout-desktop-container"
        id="desktop-content"
        style={{
          width: contentSize.width,
          height: contentSize.height,
        }}
      >
        <HeaderDesktop/>
        <SiderLeft />
        <SiderRight projects={projects} />
        <FooterDesktop/>
      </div>

      {/*
        Putting page content outside of layout-desktop-container allows us to utilize the full viewport.
        However, if you need to position content within the area marked out by header, footer, siderLeft and siderRight,
        you'll have to manually compute the available space, or refactor as necessary.
      */}
      {children}
    </div>
  );
};
